import React, { useState, useEffect } from 'react';
import axios from '../utils/axiosConfig';

function EmailLimitRequests() {
  const [requests, setRequests] = useState([]);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  // Per-request approval amount, keyed by request id.
  const [limitInputs, setLimitInputs] = useState({});
  const [busyId, setBusyId] = useState(null);

  useEffect(() => {
    fetchRequests(statusFilter);
  }, [statusFilter]);

  const fetchRequests = async (status) => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await axios.get('/api/admin/email-limit-requests', {
        params: { status }
      });
      setRequests(response.data);
      // Seed each input with what the vendor asked for.
      const seeded = {};
      response.data.forEach(r => {
        if (r.requested_limit) seeded[r.id] = String(r.requested_limit);
      });
      setLimitInputs(seeded);
    } catch (err) {
      console.error('Error fetching email limit requests:', err);
      setError(err.response?.data?.error || 'Error loading requests');
    } finally {
      setIsLoading(false);
    }
  };

  const resolve = async (request, action) => {
    const newLimit = limitInputs[request.id];

    if (action === 'approve' && (!newLimit || Number(newLimit) <= 0)) {
      alert('Enter the new monthly limit before approving.');
      return;
    }

    const confirmText = action === 'approve'
      ? `Set ${request.business_name}'s limit to ${newLimit} emails per month?`
      : `Decline the request from ${request.business_name}?`;
    if (!window.confirm(confirmText)) return;

    setBusyId(request.id);
    try {
      await axios.post(`/api/admin/email-limit-requests/${request.id}/resolve`, {
        action,
        newLimit: action === 'approve' ? newLimit : undefined
      });
      await fetchRequests(statusFilter);
    } catch (err) {
      console.error('Error resolving request:', err);
      alert(err.response?.data?.error || 'Error resolving request');
    } finally {
      setBusyId(null);
    }
  };

  const formatDate = (value) =>
    value ? new Date(value).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }) : '-';

  return (
    <div className="email-limit-requests">
      <h2>Email Limit Requests</h2>

      <div className="search-section">
        <div className="search-input-group">
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="declined">Declined</option>
            <option value="all">All</option>
          </select>
          <button type="button" onClick={() => fetchRequests(statusFilter)} disabled={isLoading}>
            {isLoading ? 'Loading...' : 'Refresh'}
          </button>
        </div>
        {error && <div className="search-error">{error}</div>}
      </div>

      {isLoading ? (
        <div className="loading">Loading requests...</div>
      ) : requests.length === 0 ? (
        <p>No {statusFilter === 'all' ? '' : statusFilter} requests.</p>
      ) : (
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>Business</th>
                <th>Contact</th>
                <th>Current limit</th>
                <th>Sent this month</th>
                <th>Requested</th>
                <th>Message</th>
                <th>Received</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {requests.map(request => (
                <tr key={request.id}>
                  <td>{request.business_name}</td>
                  <td>{request.contact_email || '-'}</td>
                  <td>{request.vendor_current_limit}</td>
                  <td>{request.vendor_emails_sent}</td>
                  <td>{request.requested_limit || 'not specified'}</td>
                  <td>{request.message || '-'}</td>
                  <td>{formatDate(request.created_at)}</td>
                  <td>
                    {request.status}
                    {request.status === 'approved' && request.approved_limit
                      ? ` (${request.approved_limit})`
                      : ''}
                  </td>
                  <td>
                    {request.status === 'pending' ? (
                      <div className="search-input-group">
                        <input
                          type="number"
                          min="1"
                          value={limitInputs[request.id] || ''}
                          onChange={(e) =>
                            setLimitInputs(prev => ({ ...prev, [request.id]: e.target.value }))
                          }
                          placeholder="New limit"
                          disabled={busyId === request.id}
                        />
                        <button
                          type="button"
                          onClick={() => resolve(request, 'approve')}
                          disabled={busyId === request.id}
                        >
                          {busyId === request.id ? 'Saving...' : 'Approve'}
                        </button>
                        <button
                          type="button"
                          className="remove-test-btn"
                          onClick={() => resolve(request, 'decline')}
                          disabled={busyId === request.id}
                        >
                          Decline
                        </button>
                      </div>
                    ) : (
                      <span>{formatDate(request.resolved_at)}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default EmailLimitRequests;
